import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { paths } from "../config/constants";

import AutoAwesomeIcon          from "@mui/icons-material/AutoAwesome";
import CheckIcon                from "@mui/icons-material/Check";
import WarningAmberIcon         from "@mui/icons-material/WarningAmber";
import AccessTimeIcon           from "@mui/icons-material/AccessTime";
import AirlineSeatReclineNormalIcon from "@mui/icons-material/AirlineSeatReclineNormal";
import LockIcon                 from "@mui/icons-material/Lock";
import SupportAgentIcon         from "@mui/icons-material/SupportAgent";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import LocalHospitalIcon        from "@mui/icons-material/LocalHospital";
import EventAvailableIcon       from "@mui/icons-material/EventAvailable";
import DirectionsCarFilledIcon  from "@mui/icons-material/DirectionsCarFilled";
import HealingIcon              from "@mui/icons-material/Healing";
import WaterIcon                from "@mui/icons-material/Water";
import MedicalServicesIcon      from "@mui/icons-material/MedicalServices";
import HomeIcon                 from "@mui/icons-material/Home";
import TravelExploreIcon        from "@mui/icons-material/TravelExplore";
import FlightIcon               from "@mui/icons-material/Flight";
import PinDropIcon              from "@mui/icons-material/PinDrop";
import TaskAltIcon              from "@mui/icons-material/TaskAlt";
import PersonIcon               from "@mui/icons-material/Person";
import AccessibleIcon           from "@mui/icons-material/Accessible";
import FamilyRestroomIcon       from "@mui/icons-material/FamilyRestroom";
import ExitToAppIcon            from "@mui/icons-material/ExitToApp";
import RepeatIcon               from "@mui/icons-material/Repeat";
import BusinessCenterIcon       from "@mui/icons-material/BusinessCenter";
import PublicIcon               from "@mui/icons-material/Public";
import FormatQuoteIcon          from "@mui/icons-material/FormatQuote";
import StarIcon                 from "@mui/icons-material/Star";
import KeyboardArrowDownIcon    from "@mui/icons-material/KeyboardArrowDown";
import ArrowForwardIcon         from "@mui/icons-material/ArrowForward";
import EastIcon                 from "@mui/icons-material/East";

import patientHeroImage from "../Assets/Images/servicesImages/patientTransfer.jpg";

export default function PatientTransfer() {
  const nav = useNavigate();
  const { t } = useTranslation("patientTransfer");
  const [openFaq, setOpenFaq] = useState(0);
  const toggleFaq = (i) => setOpenFaq((prev) => (prev === i ? -1 : i));

  /* ── Data arrays — all i18n driven ─────────────────────── */

  const fiveThings = [
    { icon: <AccessTimeIcon fontSize="small" />,          title: t("five-0-title"), text: t("five-0-text") },
    { icon: <AirlineSeatReclineNormalIcon fontSize="small" />, title: t("five-1-title"), text: t("five-1-text") },
    { icon: <LockIcon fontSize="small" />,                title: t("five-2-title"), text: t("five-2-text") },
    { icon: <SupportAgentIcon fontSize="small" />,        title: t("five-3-title"), text: t("five-3-text") },
    { icon: <SentimentSatisfiedAltIcon fontSize="small" />, title: t("five-4-title"), text: t("five-4-text") },
  ];

  const bookingSteps = [
    { icon: <PinDropIcon />,              title: t("step-0-title"), text: t("step-0-text") },
    { icon: <TaskAltIcon />,              title: t("step-1-title"), text: t("step-1-text") },
    { icon: <DirectionsCarFilledIcon />,  title: t("step-2-title"), text: t("step-2-text") },
    { icon: <AirlineSeatReclineNormalIcon />, title: t("step-3-title"), text: t("step-3-text") },
    { icon: <LocalHospitalIcon />,        title: t("step-4-title"), text: t("step-4-text") },
  ];

  const serviceTypes = [
    { icon: <LocalHospitalIcon fontSize="small" />,    title: t("service-hospital-title"),     text: t("service-hospital-text") },
    { icon: <EventAvailableIcon fontSize="small" />,   title: t("service-appointment-title"),  text: t("service-appointment-text") },
    { icon: <ExitToAppIcon fontSize="small" />,        title: t("service-discharge-title"),    text: t("service-discharge-text") },
    { icon: <HealingIcon fontSize="small" />,          title: t("service-rehab-title"),        text: t("service-rehab-text") },
    { icon: <WaterIcon fontSize="small" />,            title: t("service-dialysis-title"),     text: t("service-dialysis-text") },
    { icon: <MedicalServicesIcon fontSize="small" />,  title: t("service-cancer-title"),       text: t("service-cancer-text") },
    { icon: <HomeIcon fontSize="small" />,             title: t("service-nursinghome-title"),  text: t("service-nursinghome-text") },
    { icon: <TravelExploreIcon fontSize="small" />,    title: t("service-tourism-title"),      text: t("service-tourism-text") },
    { icon: <FlightIcon fontSize="small" />,           title: t("service-airport-title"),      text: t("service-airport-text") },
  ];

  const vehicleRows = [
    { type: t("vehicle-0-type"), idealFor: t("vehicle-0-idealfor"), passengers: t("vehicle-0-passengers") },
    { type: t("vehicle-1-type"), idealFor: t("vehicle-1-idealfor"), passengers: t("vehicle-1-passengers") },
    { type: t("vehicle-2-type"), idealFor: t("vehicle-2-idealfor"), passengers: t("vehicle-2-passengers") },
    { type: t("vehicle-3-type"), idealFor: t("vehicle-3-idealfor"), passengers: t("vehicle-3-passengers") },
    { type: t("vehicle-4-type"), idealFor: t("vehicle-4-idealfor"), passengers: t("vehicle-4-passengers") },
    { type: t("vehicle-5-type"), idealFor: t("vehicle-5-idealfor"), passengers: t("vehicle-5-passengers") },
  ];

  const whoBooks = [
    { icon: <PersonIcon fontSize="small" />,       title: t("who-0-title"), text: t("who-0-text") },
    { icon: <AccessibleIcon fontSize="small" />,   title: t("who-1-title"), text: t("who-1-text") },
    { icon: <FamilyRestroomIcon fontSize="small" />, title: t("who-2-title"), text: t("who-2-text") },
    { icon: <ExitToAppIcon fontSize="small" />,    title: t("who-3-title"), text: t("who-3-text") },
    { icon: <RepeatIcon fontSize="small" />,       title: t("who-4-title"), text: t("who-4-text") },
    { icon: <BusinessCenterIcon fontSize="small" />, title: t("who-5-title"), text: t("who-5-text") },
  ];

  const comparisonRows = [
    { feature: t("compare-row-0-feature"), bt: t("compare-row-0-bt"), taxi: t("compare-row-0-taxi"), ambulance: t("compare-row-0-ambulance") },
    { feature: t("compare-row-1-feature"), bt: t("compare-row-1-bt"), taxi: t("compare-row-1-taxi"), ambulance: t("compare-row-1-ambulance") },
    { feature: t("compare-row-2-feature"), bt: t("compare-row-2-bt"), taxi: t("compare-row-2-taxi"), ambulance: t("compare-row-2-ambulance") },
    { feature: t("compare-row-3-feature"), bt: t("compare-row-3-bt"), taxi: t("compare-row-3-taxi"), ambulance: t("compare-row-3-ambulance") },
    { feature: t("compare-row-4-feature"), bt: t("compare-row-4-bt"), taxi: t("compare-row-4-taxi"), ambulance: t("compare-row-4-ambulance") },
    { feature: t("compare-row-5-feature"), bt: t("compare-row-5-bt"), taxi: t("compare-row-5-taxi"), ambulance: t("compare-row-5-ambulance") },
    { feature: t("compare-row-6-feature"), bt: t("compare-row-6-bt"), taxi: t("compare-row-6-taxi"), ambulance: t("compare-row-6-ambulance") },
    { feature: t("compare-row-7-feature"), bt: t("compare-row-7-bt"), taxi: t("compare-row-7-taxi"), ambulance: t("compare-row-7-ambulance") },
    { feature: t("compare-row-8-feature"), bt: t("compare-row-8-bt"), taxi: t("compare-row-8-taxi"), ambulance: t("compare-row-8-ambulance") },
    { feature: t("compare-row-9-feature"), bt: t("compare-row-9-bt"), taxi: t("compare-row-9-taxi"), ambulance: t("compare-row-9-ambulance") },
  ];

  const testimonials = [
    { quote: t("testimonial-0-quote"), name: t("testimonial-0-name"), role: t("testimonial-0-role") },
    { quote: t("testimonial-1-quote"), name: t("testimonial-1-name"), role: t("testimonial-1-role") },
    { quote: t("testimonial-2-quote"), name: t("testimonial-2-name"), role: t("testimonial-2-role") },
  ];

  const faqItems = [
    { question: t("faq-0-q"),  answer: t("faq-0-a")  },
    { question: t("faq-1-q"),  answer: t("faq-1-a")  },
    { question: t("faq-2-q"),  answer: t("faq-2-a")  },
    { question: t("faq-3-q"),  answer: t("faq-3-a")  },
    { question: t("faq-4-q"),  answer: t("faq-4-a")  },
    { question: t("faq-5-q"),  answer: t("faq-5-a")  },
    { question: t("faq-6-q"),  answer: t("faq-6-a")  },
    { question: t("faq-7-q"),  answer: t("faq-7-a")  },
    { question: t("faq-8-q"),  answer: t("faq-8-a")  },
    { question: t("faq-9-q"),  answer: t("faq-9-a")  },
    { question: t("faq-10-q"), answer: t("faq-10-a") },
    { question: t("faq-11-q"), answer: t("faq-11-a") },
    { question: t("faq-12-q"), answer: t("faq-12-a") },
    { question: t("faq-13-q"), answer: t("faq-13-a") },
    { question: t("faq-14-q"), answer: t("faq-14-a") },
  ];

  const relatedServices = [
    { title: t("related-0-title"), text: t("related-0-text"), href: paths.cityToCityRides },
    { title: t("related-1-title"), text: t("related-1-text"), href: paths.airportTransfer },
    { title: t("related-2-title"), text: t("related-2-text"), href: paths.businessTransfer },
    { title: t("related-3-title"), text: t("related-3-text"), href: paths.tourAndEventTransfer },
    { title: t("related-4-title"), text: t("related-4-text"), href: paths.baggageTransfer },
    { title: t("related-5-title"), text: t("related-5-text"), href: paths.petTransfer },
    { title: t("related-6-title"), text: t("related-6-text"), href: paths.vipService },
  ];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{t("meta-title")}</title>
        <meta name="description" content={t("meta-description")} />
        <link rel="canonical" href="https://blu.taxi/services/patient-transfer" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t("meta-og-title")} />
        <meta property="og:description" content={t("meta-og-description")} />
        <meta property="og:url" content="https://blu.taxi/services/patient-transfer" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
      </Helmet>

      <div className="bg-white homeds">

        {/* ── Hero ── */}
        <div className="pat-hero">
          <div className="pat-hero-bg-image" />
          <div className="pat-hero-overlay" />

          <div className="pat-hero-inner">
            <span className="pat-hero-badge">
              <span className="pat-hero-badge-icon"><AutoAwesomeIcon fontSize="inherit" /></span>
              {t("hero-badge")}
            </span>

            <h1 className="pat-hero-title">
              {t("hero-title-line1")}
              <br />
              <span className="pat-hero-title-accent">{t("hero-title-accent1")}</span>{" "}
              <span className="pat-hero-title-accent">{t("hero-title-accent2")}</span>{" "}
              {t("hero-title-line2end")}
            </h1>

            <p className="pat-hero-description">{t("hero-description")}</p>

            {/* <div className="pat-hero-actions">
              <button type="button" className="pat-hero-btn-primary" onClick={() => nav("/")}>
                Book Patient Transfer <EastIcon fontSize="small" />
              </button>
            </div> */}

            <div className="pat-hero-trust-bar">
              {[t("hero-trust-1"), t("hero-trust-2"), t("hero-trust-3"), t("hero-trust-4")].map((item) => (
                <span className="pat-hero-trust-item" key={item}>
                  <span className="pat-hero-trust-icon"><CheckIcon fontSize="inherit" /></span>
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="pat-hero-strip">
            <p className="pat-hero-strip-text">{t("hero-strip")}</p>
          </div>
        </div>

        <section className="pat-page">
          <div className="pat-container">

            {/* ── Important Notice ── */}
            <div className="pat-section" style={{ paddingBottom: 0 }}>
              <div className="pat-notice">
                <span className="pat-notice-icon" aria-hidden="true">
                  <WarningAmberIcon />
                </span>
                <div>
                  <p className="pat-notice-eyebrow">{t("notice-eyebrow")}</p>
                  <p className="pat-notice-heading">{t("notice-heading")}</p>
                  <p className="pat-notice-text">{t("notice-text")}</p>
                </div>
              </div>
            </div>

            {/* ── Definition ── */}
            <div className="pat-section">
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("definition-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("definition-heading")}</h2>
              <p className="pat-intro-text">{t("definition-text")}</p>
            </div>

            {/* ── Why It Matters ── */}
            <div className="pat-section" style={{ paddingTop: 0 }}>
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("why-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("why-heading")}</h2>
              <p className="pat-intro-text">{t("why-text")}</p>
            </div>

            {/* ── Five Things (dark band) ── */}
            <div className="pat-section pat-section--onDark" style={{ borderRadius: 24 }}>
              <div className="pat-container" style={{ padding: "0 24px" }}>
                <p className="pat-eyebrow pat-eyebrow--onDark">
                  <span className="pat-eyebrow-bar" />{t("five-eyebrow")}
                </p>
                <h2 className="pat-heading pat-heading--left" style={{ color: "#fff" }}>
                  {t("five-heading")}
                </h2>
                <div className="pat-five-grid">
                  {fiveThings.map((item) => (
                    <div className="pat-five-card" key={item.title}>
                      <span className="pat-five-icon" aria-hidden="true">{item.icon}</span>
                      <div>
                        <p className="pat-five-title">{item.title}</p>
                        <p className="pat-five-text">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Booking Steps ── */}
            <div className="pat-section">
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("steps-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("steps-heading")}</h2>
              <p className="pat-intro-text">{t("steps-intro")}</p>

              <div className="pat-steps-list">
                {bookingSteps.map((step, i) => (
                  <div className="pat-step" key={step.title}>
                    <div className="pat-step-icon-wrap">
                      <span className="pat-step-icon">{step.icon}</span>
                      <span className="pat-step-number">{t("step-label")} {i + 1}</span>
                    </div>
                    <div>
                      <h3 className="pat-step-title">{step.title}</h3>
                      <p className="pat-step-text">{step.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Service Types ── */}
            <div className="pat-section" style={{ paddingTop: 0 }}>
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("services-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("services-heading")}</h2>

              <div className="pat-services-grid">
                {serviceTypes.map((item) => (
                  <div className="pat-service-card" key={item.title}>
                    <span className="pat-service-icon" aria-hidden="true">{item.icon}</span>
                    <div>
                      <p className="pat-service-title">{item.title}</p>
                      <p className="pat-service-text">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Vehicle Table ── */}
            <div className="pat-section" style={{ paddingTop: 0 }}>
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("vehicles-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("vehicles-heading")}</h2>
              <p className="pat-intro-text">{t("vehicles-intro")}</p>

              <div className="pat-table-wrap">
                <table className="pat-table">
                  <thead>
                    <tr>
                      <th>{t("vehicle-col-type")}</th>
                      <th>{t("vehicle-col-idealfor")}</th>
                      <th>{t("vehicle-col-passengers")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicleRows.map((row) => (
                      <tr key={row.type}>
                        <td style={{ fontWeight: 700 }}>{row.type}</td>
                        <td>{row.idealFor}</td>
                        <td>{row.passengers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Safety Standards ── */}
            <div className="pat-section" style={{ paddingTop: 0 }}>
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("safety-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("safety-heading")}</h2>
              <p className="pat-intro-text">{t("safety-text")}</p>
              <div className="pat-safety-notice">
                <span className="pat-safety-notice-icon" aria-hidden="true">
                  <WarningAmberIcon style={{ fontSize: 18 }} />
                </span>
                <p className="pat-safety-notice-text">{t("safety-notice")}</p>
              </div>
            </div>

            {/* ── Who Uses ── */}
            <div className="pat-section" style={{ paddingTop: 0 }}>
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("who-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("who-heading")}</h2>
              <p className="pat-intro-text">{t("who-intro")}</p>

              <div className="pat-who-grid">
                {whoBooks.map((item) => (
                  <div className="pat-who-card" key={item.title}>
                    <span className="pat-who-icon" aria-hidden="true">{item.icon}</span>
                    <div>
                      <p className="pat-who-title">{item.title}</p>
                      <p className="pat-who-text">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

              {/* ── Coverage band ── */}
              <div className="pat-section" style={{ paddingTop: 0 }}>
              <div className="pat-availability-band">
                <div className="pat-availability-icon" aria-hidden="true">
                  <PublicIcon />
                </div>
                <div>
                  <p className="pat-eyebrow pat-eyebrow--onDark" style={{ marginBottom: 10 }}>
                    <span className="pat-eyebrow-bar" />{t("coverage-eyebrow")}
                  </p>
                  <h2 className="pat-heading pat-heading--left" style={{ color: "#fff", marginBottom: 12 }}>
                    {t("coverage-heading")}
                  </h2>
                  <p style={{ color: "#c7d2e6", fontSize: "0.95rem", lineHeight: 1.75, maxWidth: 680, margin: "0 0 24px" }}>
                    {t("coverage-text")}
                  </p>
                  <Link className="pat-availability-link" to="/">
                    {t("coverage-link")} <ArrowForwardIcon style={{ fontSize: 15 }} />
                  </Link>
                </div>
              </div>
            </div>

            {/* ── Comparison ── */}
            <div className="pat-section" style={{ paddingTop: 0 }}>
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("compare-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("compare-heading")}</h2>
              <p className="pat-intro-text">{t("compare-intro")}</p>

              <div className="pat-table-wrap">
                <table className="pat-table pat-table--comparison">
                  <thead>
                    <tr>
                      <th>{t("compare-col-feature")}</th>
                      <th className="pat-table-col--highlight">{t("compare-col-bt")}</th>
                      <th>{t("compare-col-taxi")}</th>
                      <th>{t("compare-col-ambulance")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map((row) => (
                      <tr key={row.feature}>
                        <td style={{ fontWeight: 700 }}>{row.feature}</td>
                        <td className="pat-table-col--highlight">{row.bt}</td>
                        <td>{row.taxi}</td>
                        <td>{row.ambulance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Testimonials ── */}
            <div className="pat-section" style={{ paddingTop: 0 }}>
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("testimonials-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("testimonials-heading")}</h2>

              <div className="pat-testimonials-grid">
                {testimonials.map((item) => (
                  <div className="pat-testimonial-card" key={item.name}>
                    <span className="pat-testimonial-quote-icon" aria-hidden="true">
                      <FormatQuoteIcon />
                    </span>
                    <p className="pat-testimonial-text">&ldquo;{item.quote}&rdquo;</p>
                    <div className="pat-testimonial-author">
                      <p className="pat-testimonial-name">{item.name}</p>
                      <p className="pat-testimonial-role">{item.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          

            {/* ── CTA band ── */}
            <div className="pat-section" style={{ paddingTop: 0, paddingBottom: 0 }}>
              <div className="pat-cta-band">
                <div className="pat-cta-text">
                  <h3>
                    {t("cta-heading")}{" "}
                    <span className="pat-cta-accent">{t("cta-heading-accent")}</span>
                  </h3>
                  <p>{t("cta-body")}</p>
                </div>
                <div className="pat-cta-buttons">
                  <button className="pat-cta-button" type="button" onClick={() => nav("/")}>
                    {t("cta-btn-primary")} <ArrowForwardIcon style={{ fontSize: 16 }} />
                  </button>
                  {/* <button className="pat-cta-button pat-cta-button--ghost" type="button">
                    {t("cta-btn-secondary")}
                  </button> */}
                </div>
              </div>
            </div>

            {/* ── FAQ ── */}
            <div className="pat-section">
              <div className="pat-faq-grid">
                <div>
                  <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("faq-eyebrow")}</p>
                  <h2 className="pat-heading pat-heading--left">{t("faq-heading")}</h2>
                  <p className="pat-faq-intro-text">{t("faq-intro")}</p>
                  <div className="pat-faq-support-card">
                    <span className="pat-faq-support-icon" aria-hidden="true">
                      <StarIcon style={{ fontSize: 18 }} />
                    </span>
                    <p className="pat-faq-support-text">{t("faq-support-text")}</p>
                  </div>
                </div>

                <div className="pat-faq-list">
                  {faqItems.map((item, i) => {
                    const isOpen = openFaq === i;
                    return (
                      <div
                        className={`pat-faq-item ${isOpen ? "pat-faq-item--open" : ""}`}
                        key={item.question}
                      >
                        <button
                          type="button"
                          className="pat-faq-question"
                          aria-expanded={isOpen}
                          onClick={() => toggleFaq(i)}
                        >
                          <span>{item.question}</span>
                          <span className="pat-faq-toggle">
                            <KeyboardArrowDownIcon fontSize="small" />
                          </span>
                        </button>
                        <div className="pat-faq-answer-wrap">
                          <div className="pat-faq-answer-inner">
                            <p className="pat-faq-answer">{item.answer}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Related ── */}
            <div className="pat-section" style={{ paddingTop: 0, paddingBottom: 0 }}>
              <p className="pat-eyebrow"><span className="pat-eyebrow-bar" />{t("related-eyebrow")}</p>
              <h2 className="pat-heading pat-heading--left">{t("related-heading")}</h2>

              <div className="pat-related-grid">
                {relatedServices.map((s) => (
                  <Link className="pat-related-card" to={s.href} key={s.title}>
                    <div className="pat-related-card-header">
                      <p className="pat-related-card-title">{s.title}</p>
                      <span className="pat-related-card-arrow" aria-hidden="true">
                        <ArrowForwardIcon fontSize="inherit" />
                      </span>
                    </div>
                    <p className="pat-related-card-text">{s.text}</p>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </section>
      </div>
    </>
  );
}